import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { PageHead, Switch } from 'views/design-system/components';

import { ICountryRow, useCountryCoverage } from '../data/use-tm';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import '../txn-surface.scss';
import './country-coverage-deck.scss';

/**
 * COUNTRY COVERAGE — which countries this business will accept activity from.
 *
 * A settings page, not a queue, and it is built like one: the whole list arrives
 * in a single response and is filtered in the browser. Paging it would make the
 * tab counts and any future bulk action mean "…on this page", which is the kind
 * of half-applied compliance control that is worse than none.
 *
 * WHAT A TOGGLE DOES, because the page must not be coy about it: OFF is a hard
 * decline. A transaction naming that country on either side — subject or
 * counterparty, and on a trade either leg — is DECLINED by rule `tm-cc-001`, not
 * flagged for review. ON does not mean "vetted"; it means "no country rule
 * applies", and every other rule still runs.
 */

/**
 * THE TILES ARE THE FILTER.
 *
 * There used to be a row of tiles and, directly beneath it, a row of tabs
 * carrying the same four numbers — Covered, Embargoed and Turned off each
 * appeared twice, three lines apart, and a reader had to work out whether the
 * two rows meant different things. They did not. One control now, and clicking a
 * summary filters to exactly the rows it counts, which is what most people try
 * first anyway.
 *
 * COLOUR CARRIES MEANING RATHER THAN ALARM. The old palette painted Declining,
 * Embargoed and High risk in near-identical pinks, so three unrelated ideas — a
 * business's own choice, a legal prohibition, and a risk rating — all read as
 * the same kind of bad. Now: green for covered, neutral for a choice the
 * business made, red reserved for the legal block, and the risk tiers on their
 * own amber ramp.
 */
const TILES = [
	{ key: 'all', label: 'All countries', hint: 'every country we know of', tone: 'neutral' },
	{ key: 'enabled', label: 'Covered', hint: 'accepted, monitored normally', tone: 'ok' },
	{ key: 'disabled', label: 'Declining', hint: 'turned off by this business', tone: 'off' },
	{ key: 'locked', label: 'Embargoed', hint: 'US embargo · cannot be enabled', tone: 'stop' },
	{ key: 'high', label: 'High risk', hint: 'FATF call for action', tone: 'hi' },
	{ key: 'medium', label: 'Medium risk', hint: 'FATF increased monitoring', tone: 'md' },
] as const;

type ITab = (typeof TILES)[number]['key'];

export const CountryCoverageDeck: FC = () => {
	const { rows, counts, tierDefaults, regions, loading, failed, setEnabled, setRisk, reload } =
		useCountryCoverage();
	const [tab, setTab] = useState<ITab>('all');
	const [q, setQ] = useState('');
	/* The row mid-flight, so a slow PATCH cannot be double-submitted and the user
	   can see which one is settling. */
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	/* THE ROW BEING EDITED. Only one row carries inputs at a time — 240 permanent
	   selects and number fields would drown the list and make the page read as a
	   form rather than a register of what is covered. */
	const [editing, setEditing] = useState<string | null>(null);
	const [draftTier, setDraftTier] = useState<'high' | 'medium' | 'low'>('low');
	/**
	 * HELD AS TEXT, not as a number, and that is the whole fix.
	 *
	 * With a numeric state the field could not be cleared: typing 25 after the
	 * initial 0 leaves the DOM holding "025", `Number("025")` is 25, and React then
	 * compares its own previous value (25) with the new one (25), sees no change
	 * and never rewrites the input — so the stray zero sticks and no amount of
	 * typing removes it. Keeping the raw string means the field always shows
	 * exactly what was typed, including empty while the user is mid-edit, and the
	 * value is parsed once on save.
	 */
	const [draftScore, setDraftScore] = useState<string>('0');

	useEffect(() => {
		reload();
	}, [reload]);

	const shown = useMemo(() => {
		const needle = q.trim().toLowerCase();
		return rows.filter(r => {
			if (tab === 'locked' && !r.locked) return false;
			if (tab === 'disabled' && (r.locked || r.enabled)) return false;
			if (tab === 'enabled' && (r.locked || !r.enabled)) return false;
			if (tab === 'high' && r.riskTier !== 'high') return false;
			if (tab === 'medium' && r.riskTier !== 'medium') return false;
			if (!needle) return true;
			return (
				r.name.toLowerCase().includes(needle) || r.code.toLowerCase().includes(needle)
			);
		});
	}, [rows, tab, q]);

	const onToggle = useCallback(
		async (code: string, next: boolean) => {
			setBusy(code);
			setError(null);
			const res = await setEnabled(code, next);
			/* The server refuses an embargoed country even though the switch is
			   locked in the UI — a disabled control stops nobody with a terminal.
			   When it refuses, it names the programme, and that message is worth
			   showing verbatim rather than replacing with "failed". */
			if (res?.error) setError(res.error);
			setBusy(null);
		},
		[setEnabled]
	);

	const beginEdit = useCallback((r: ICountryRow) => {
		setEditing(r.code);
		setDraftTier(r.riskTier);
		setDraftScore(String(r.riskScore));
	}, []);

	/* PICKING A TIER SNAPS THE SCORE to that tier's default, but typing a score
	   never relabels the tier. They are independent axes: "how bad is this" and
	   "how many points", and silently rewriting one when the user edits the other
	   is how a compliance officer stops trusting the control. */
	const pickTier = useCallback(
		(t: 'high' | 'medium' | 'low') => {
			setDraftTier(t);
			setDraftScore(String(tierDefaults[t] ?? 0));
		},
		[tierDefaults]
	);

	const saveRisk = useCallback(
		async (code: string) => {
			/* Parsed once, here. An empty field means zero rather than an error —
			   clearing the box to say "this country costs nothing" is a reasonable
			   thing to do and should not be met with a validation message. */
			const n = draftScore.trim() === '' ? 0 : Number(draftScore);
			if (!Number.isFinite(n) || n < 0 || n > 100) {
				setError('A risk score must be a whole number between 0 and 100.');
				return;
			}
			setBusy(code);
			const res = await setRisk(code, draftTier, Math.round(n));
			if (res?.error) setError(res.error);
			setBusy(null);
			setEditing(null);
		},
		[setRisk, draftTier, draftScore]
	);

	const revertRisk = useCallback(
		async (code: string) => {
			setBusy(code);
			/* Null, not the default numbers — a reverted row goes back to FOLLOWING
			   the platform default, so it moves with the next FATF plenary instead
			   of being pinned to today's values while looking identical. */
			const res = await setRisk(code, null, null);
			if (res?.error) setError(res.error);
			setBusy(null);
			setEditing(null);
		},
		[setRisk]
	);

	return (
		<div className="sv2-scope sv2 txn-deck cc-deck">
			<PageHead
				title="Country coverage"
				sub="Which countries this business accepts activity from. Turning a country off DECLINES any transaction naming it — on either side, and on either leg of a trade."
			/>
			{/* Tiles that filter. See TILES above for why this is one row and not two. */}
			<div className="tiles">
				{TILES.map(t => {
					const n =
						t.key === 'all'
							? counts.total
							: t.key === 'enabled'
								? counts.enabled
								: t.key === 'disabled'
									? counts.disabled
									: t.key === 'locked'
										? counts.locked
										: t.key === 'high'
											? counts.high
											: counts.medium;
					return (
						<button
							key={t.key}
							type="button"
							className={`tile tl-${t.tone}${tab === t.key ? ' on' : ''}`}
							aria-pressed={tab === t.key}
							onClick={() => setTab(t.key)}
						>
							<div className="l"><span className="mk" />{t.label}</div>
							<div className="n">{n}</div>
							<div className="d">{t.hint}</div>
						</button>
					);
				})}
			</div>





			{/* THE REGIONS WE CANNOT ENFORCE, said out loud rather than left as a
			    silent gap. Crimea, Donetsk and Luhansk are comprehensively
			    sanctioned REGIONS of Ukraine; a country-level toggle cannot express
			    a sub-country programme, and no region field exists anywhere in the
			    transaction pipeline. Disclosing it is honest; omitting it would let
			    someone believe this page covers them. */}
			{regions.length ? (
				<div className="ccnote">
					<b>Not enforceable here.</b> {regions.join(' · ')} are comprehensively
					sanctioned regions rather than countries. A country toggle cannot represent
					them, and transactions carry no region, so they are not covered by this
					page — screen for them through sanctions screening.
				</div>
			) : null}

			<div className="decksearch">
				<label className="srch" title="Searches country name and ISO code">
					<svg viewBox="0 0 24 24">
						<circle cx="11" cy="11" r="7" />
						<line x1="16.5" y1="16.5" x2="21" y2="21" />
					</svg>
					<input
						placeholder="Search by country or code"
						value={q}
						onChange={e => setQ(e.target.value)}
					/>
				</label>
				{q.trim() ? (
					<span className="hint">
						{shown.length} of {rows.length}
						<button className="clr" type="button" onClick={() => setQ('')}>
							clear
						</button>
					</span>
				) : null}
			</div>
			{error ? <div className="ccerr">{error}</div> : null}
			<div className="panel">
				<div className="ph">
					Countries <span className="r">{shown.length}</span>
				</div>
				<table className="tbl cctbl">
					<thead>
						<tr>
							<th>Country</th>
							<th>Code</th>
							<th>Status</th>
							<th className="ccrisk">Rating</th>
							<th className="ccscore">Score</th>
							<th className="ccsw">Covered</th>
						</tr>
					</thead>
					<tbody>
						{shown.length === 0 && (
							<tr>
								<td colSpan={6} className="empty">
									{loading
										? 'Loading countries…'
										: failed
											? 'Could not reach transaction monitoring.'
											: q.trim()
												? `No country matches “${q.trim()}”.`
												: 'No countries in this tab.'}
								</td>
							</tr>
						)}
						{shown.map(r => (
							<tr key={r.code} className={r.locked ? 'lockedrow' : ''}>
								<td>{r.name}</td>
								<td className="mono">{r.code}</td>
								<td>
									{r.locked ? (
										<span
											className="nd locked"
											title={`${r.program ?? 'OFAC'}${r.citation ? ` · ${r.citation}` : ''}`}
										>
											US embargo
										</span>
									) : r.enabled ? (
										<span className="nd pass">covered</span>
									) : (
										<span className="nd fail">declines</span>
									)}
								</td>
								<td className="ccrisk">
									{/* LOW GETS NO CHIP. 213 of 240 countries are low, and chipping
									    every one of them would turn the page into a wall of colour in
									    which the 27 that matter stop standing out — the opposite of
									    what a rating is for. */}
									{editing === r.code ? (
										<span className="tierpick">
											{(['high', 'medium', 'low'] as const).map(t => (
												<button
													key={t}
													type="button"
													className={`pre${draftTier === t ? ' on' : ''}`}
													onClick={() => pickTier(t)}
												>
													{t}
												</button>
											))}
										</span>
									) : (
										<button
											type="button"
											className="tierbtn"
											title={
												(r.riskNote ?? 'Not on any watchlist — rated low by default.') +
												(r.overridden
													? `  ·  This business has pinned its own rating; the platform default is ${r.defaultTier} / ${r.defaultScore}.`
													: '  ·  Following the platform default, so it moves when the list does.')
											}
											onClick={() => beginEdit(r)}
										>
											{r.riskTier === 'low' ? (
												<span className="lowtier">low</span>
											) : (
												<span className={`nd ${r.riskTier === 'high' ? 'fail' : 'review'}`}>
													{r.riskTier}
												</span>
											)}
											{r.overridden ? <span className="bch">custom</span> : null}
										</button>
									)}
								</td>
								<td className="ccscore">
									{editing === r.code ? (
										<span className="scoreedit">
											<input
												type="text"
												inputMode="numeric"
												value={draftScore}
												/* Digits only, so the string can never become something
												   `Number()` would silently read as NaN on save. */
												onChange={e =>
													setDraftScore(e.target.value.replace(/[^0-9]/g, ''))
												}
												/* Select-all on focus: the common edit is "replace this
												   number", and having to clear it first is the friction
												   that produced the stray zero in the first place. */
												onFocus={e => e.target.select()}
												onKeyDown={e => {
													if (e.key === 'Enter') saveRisk(r.code);
													if (e.key === 'Escape') setEditing(null);
												}}
												aria-label={`Risk score for ${r.name}`}
											/>
											<button
												type="button"
												className="pre on"
												disabled={busy === r.code}
												onClick={() => saveRisk(r.code)}
											>
												save
											</button>
											{r.overridden ? (
												<button
													type="button"
													className="pre"
													title="Go back to the platform default, and keep following it as the lists change"
													disabled={busy === r.code}
													onClick={() => revertRisk(r.code)}
												>
													↺
												</button>
											) : null}
											<button type="button" className="pre" onClick={() => setEditing(null)}>
												×
											</button>
										</span>
									) : (
										<button type="button" className="scorebtn" onClick={() => beginEdit(r)}>
											{r.riskScore > 0 ? `+${r.riskScore}` : '—'}
										</button>
									)}
								</td>
								<td className="ccsw">
									<Switch
										checked={r.enabled}
										locked={r.locked}
										disabled={busy === r.code}
										label={`Cover ${r.name}`}
										title={
											r.locked
												? `${r.name} is under a comprehensive US embargo (${
														r.program ?? 'OFAC'
													}) and cannot be enabled.`
												: r.enabled
													? `Transactions naming ${r.name} are monitored normally. Turn off to decline them.`
													: `Transactions naming ${r.name} are DECLINED.`
										}
										onChange={next => onToggle(r.code, next)}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="foot">
				A country with no setting is covered — a new business starts able to transact
				everywhere except the embargoed set, because defaulting to all-off would
				silently decline its very first transaction. Embargoed countries are locked
				platform-wide and refused by the server as well as by this screen. Changing a
				toggle never re-scores past transactions: the reason a transaction was declined
				is stamped on it when it is scored, so a stored decision keeps saying what it
				was actually based on.
			</div>
		</div>
	);
};

export default CountryCoverageDeck;
